import { config } from '../config/env';
import { auth } from '../firebase';

// Interface für ein einzelnes CV-Design
export interface CvDesignSettings {
  // Farben
  leftColumnColor: string;
  nameTextColor: string;
  leftColumnTextColor: string;
  rightColumnTextColor: string;
  leftColumnTitleColor: string;
  rightColumnTitleColor: string;
  
  // Sichtbarkeits-Einstellungen
  showCoreCompetenciesInCv: boolean;
  showCertificationsInCv: boolean;
  showItSkillsInCv: boolean;
  showIndustriesInCv: boolean;
  showLanguagesInCv: boolean;
  showHobbiesInCv: boolean;
  showEducationInCv: boolean;
  showWorkExperienceInCv: boolean;
  showProfileInCv: boolean;
  showAdditionalInfoInCv: boolean;
  showProfileImageInCv: boolean;
  showContactInCv?: boolean;
  showCompanyNamesInCv: boolean;
  showIndustryInCv: boolean;
  showDateInCv: boolean;
  showProjectSearchInCv: boolean;
  showBulletPointsInCv?: boolean;
  showAdditionalWorkExperienceInCv?: boolean;
  showPhotoInCv?: boolean;
  tonalitySettings?: {
    formalitaet: number;
    selbstbewusstsein: number;
    kreativitaet: number;
    tonAktiv: number;
    tonErgebnisorientiert: number;
    tonKompakt: number;
  };
  shortProfileTextLength?: 'S' | 'M' | 'L';
  workExperienceTextLength?: 'S' | 'M' | 'L';
  sortWorkExperienceByRelevance?: boolean;
  
  // Profilbild-Einstellungen
  profileImageBorderRadius: number;
  profileImageSize: string;
  profileImageWidth: number;
  profileImagePosition: { x: number; y: number };
  profileImageAlignment: string;
  profileImageBorder: boolean;
  profileImageBorderColor: string;
  profileImageBorderThickness: number;
  
  // Namensgröße
  nameFontSize: number;
  
  // Kontakt-Display-Mode
  contactDisplayMode: 'text' | 'icon';
  
  // Header-Box-Einstellungen
  topBoxColor: string;
  topBoxAlpha?: number; // Alpha-Wert für Header-Box (0-1)
  topBoxImage: string;
  topBoxHeightMode?: 'nameBottom' | 'profileMiddle';
  
  // Linke Spalte Bild
  leftColumnImage: string;
  
  // Schriftarten
  titleFont?: string; // Schriftart für Titel (Name, Sektions-Titel)
  bodyFont?: string;  // Schriftart für Fließtext (Kontakt, Details, etc.)
  
  // Template-Name
  templateName: string;
}

// Interface für alle CV-Settings (mit 3 Templates)
export interface CvSettings {
  template1: CvDesignSettings;  // Modern Design
  template2: CvDesignSettings;  // Professional Design
  template3: CvDesignSettings;  // Classic Design
  selectedTemplate: 'template1' | 'template2' | 'template3';
  lastUpdated: string;
  version: string;
}

// Standard-Design-Settings
const createDefaultDesignSettings = (templateName: string): CvDesignSettings => ({
  // Farben
  leftColumnColor: templateName === 'Professional' ? '#2e2e2e' : '#f8f6f2', // RGB(46,46,46) für Professional
  nameTextColor: '#333333',
  leftColumnTextColor: templateName === 'Professional' ? '#ffffff' : '#333333', // Weiß für Professional
  rightColumnTextColor: '#333333',
  leftColumnTitleColor: templateName === 'Professional' ? '#ffffff' : '#333333', // Weiß für Professional
  rightColumnTitleColor: '#333333',
  
  // Sichtbarkeits-Einstellungen
  showCoreCompetenciesInCv: true,
  showCertificationsInCv: true,
  showItSkillsInCv: true,
  showIndustriesInCv: true,
  showLanguagesInCv: true,
  showHobbiesInCv: true,
  showEducationInCv: true,
  showWorkExperienceInCv: true,
  showProfileInCv: true,
  showAdditionalInfoInCv: true,
  showProfileImageInCv: true,
  showContactInCv: true,
  showCompanyNamesInCv: true,
  showIndustryInCv: true,
  showDateInCv: true,
  showProjectSearchInCv: false,
  showBulletPointsInCv: false,
  showAdditionalWorkExperienceInCv: true,
  showPhotoInCv: true,
  
  // Profilbild-Einstellungen
  profileImageBorderRadius: (templateName === 'Professional' || templateName === 'Classic') ? 0 : 50,
  profileImageSize: 'M',
  profileImageWidth: 100,
  profileImagePosition: { x: 50, y: 50 },
  profileImageAlignment: templateName === 'Professional' ? 'left' : 'center',
  profileImageBorder: false,
  profileImageBorderColor: '#333333',
  profileImageBorderThickness: 2,
  
  // Namensgröße
  nameFontSize: 36,
  
  // Kontakt-Display-Mode
  contactDisplayMode: 'text',
  
  // Header-Box-Einstellungen
  topBoxColor: templateName === 'Professional' ? '#2e2e2e' : '#f8f6f2', // Gleiche Farbe wie linke Spalte
  topBoxAlpha: 0, // Default: 100% transparent
  topBoxImage: '', // Kein Bild standardmäßig
  topBoxHeightMode: 'nameBottom',
  
  // Linke Spalte Bild
  leftColumnImage: '', // Kein Bild standardmäßig
  
  // Schriftarten
  titleFont: '"Times New Roman", Times, serif',
  bodyFont: 'Arial, sans-serif',
  
  // Tonalität
  tonalitySettings: {
    formalitaet: 70,
    selbstbewusstsein: 70,
    kreativitaet: 30,
    tonAktiv: 80,
    tonErgebnisorientiert: 80,
    tonKompakt: 50
  },
  shortProfileTextLength: 'S',
  workExperienceTextLength: 'S',
  sortWorkExperienceByRelevance: false,
  
  // Template-Name
  templateName
});

// Standard-CV-Settings mit 3 Templates
export const defaultCvSettings: CvSettings = {
  template1: createDefaultDesignSettings('Modern'),
  template2: createDefaultDesignSettings('Professional'),
  template3: createDefaultDesignSettings('Classic'),
  selectedTemplate: 'template1',
  lastUpdated: new Date().toISOString(),
  version: '2.0.0'
};

// CV-Settings pro CV speichern (userCvData/{cvId})
export const saveCvSettings = async (cvId: string, settings: CvSettings): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User nicht authentifiziert');
    }
    if (!cvId) {
      throw new Error('cvId ist erforderlich');
    }

    const idToken = await user.getIdToken();

    const settingsWithMetadata = {
      ...settings,
      lastUpdated: new Date().toISOString(),
      version: '2.0.0'
    };
    
    const response = await fetch(`${config.backend.baseUrl}/api/save-cv-settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        cvId,
        cvSettings: settingsWithMetadata
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

  } catch (error) {
    console.error('Fehler beim Speichern der CV-Settings:', error);
    throw error;
  }
};

// CV-Settings pro CV laden (userCvData/{cvId})
export const loadCvSettings = async (cvId: string): Promise<CvSettings | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    if (!cvId) {
      return null;
    }

    const idToken = await user.getIdToken();
    
    const response = await fetch(`${config.backend.baseUrl}/api/load-cv-settings?cvId=${encodeURIComponent(cvId)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (response.ok) {
      const result = await response.json();
      
      if (result.cvSettings) {
        if (result.cvSettings.version === '1.0.0' || !result.cvSettings.template1) {
          return migrateToNewStructure(result.cvSettings);
        }
        return result.cvSettings;
      }
    }

    return null;
  } catch (error) {
    console.error('Fehler beim Laden der CV-Settings:', error);
    return null;
  }
};

// Migration von alter zu neuer Struktur
const migrateToNewStructure = (oldSettings: any): CvSettings => {
  const migratedSettings = { ...oldSettings };
  
  // Erstelle 3 Templates basierend auf den alten Settings
  const baseSettings = {
    leftColumnColor: migratedSettings.leftColumnColor || '#f8f6f2',
    nameTextColor: migratedSettings.nameTextColor || '#333333',
    leftColumnTextColor: migratedSettings.leftColumnTextColor || '#333333',
    rightColumnTextColor: migratedSettings.rightColumnTextColor || '#333333',
    leftColumnTitleColor: migratedSettings.leftColumnTitleColor || '#333333',
    rightColumnTitleColor: migratedSettings.rightColumnTitleColor || '#333333',
    showCoreCompetenciesInCv: migratedSettings.showCoreCompetenciesInCv !== undefined ? migratedSettings.showCoreCompetenciesInCv : true,
    showCertificationsInCv: migratedSettings.showCertificationsInCv !== undefined ? migratedSettings.showCertificationsInCv : true,
    showItSkillsInCv: migratedSettings.showItSkillsInCv !== undefined ? migratedSettings.showItSkillsInCv : true,
    showIndustriesInCv: migratedSettings.showIndustriesInCv !== undefined ? migratedSettings.showIndustriesInCv : true,
    showLanguagesInCv: migratedSettings.showLanguagesInCv !== undefined ? migratedSettings.showLanguagesInCv : true,
    showHobbiesInCv: migratedSettings.showHobbiesInCv !== undefined ? migratedSettings.showHobbiesInCv : true,
    showEducationInCv: migratedSettings.showEducationInCv !== undefined ? migratedSettings.showEducationInCv : true,
    showWorkExperienceInCv: migratedSettings.showWorkExperienceInCv !== undefined ? migratedSettings.showWorkExperienceInCv : true,
    showProfileInCv: migratedSettings.showProfileInCv !== undefined ? migratedSettings.showProfileInCv : true,
    showAdditionalInfoInCv: migratedSettings.showAdditionalInfoInCv !== undefined ? migratedSettings.showAdditionalInfoInCv : true,
    showProfileImageInCv: migratedSettings.showProfileImageInCv !== undefined ? migratedSettings.showProfileImageInCv : true,
    showContactInCv: migratedSettings.showContactInCv !== undefined ? migratedSettings.showContactInCv : true,
    showCompanyNamesInCv: migratedSettings.showCompanyNamesInCv !== undefined ? migratedSettings.showCompanyNamesInCv : true,
    showIndustryInCv: migratedSettings.showIndustryInCv !== undefined ? migratedSettings.showIndustryInCv : true,
    showDateInCv: migratedSettings.showDateInCv !== undefined ? migratedSettings.showDateInCv : true,
    showProjectSearchInCv: migratedSettings.showProjectSearchInCv !== undefined ? migratedSettings.showProjectSearchInCv : false,
    showBulletPointsInCv: migratedSettings.showBulletPointsInCv !== undefined ? migratedSettings.showBulletPointsInCv : false,
    showAdditionalWorkExperienceInCv: migratedSettings.showAdditionalWorkExperienceInCv !== undefined ? migratedSettings.showAdditionalWorkExperienceInCv : true,
    showPhotoInCv: migratedSettings.showPhotoInCv !== undefined ? migratedSettings.showPhotoInCv : true,
    profileImageBorderRadius: migratedSettings.profileImageBorderRadius || 50,
    profileImageSize: migratedSettings.profileImageSize || 'M',
    profileImageWidth: migratedSettings.profileImageWidth || 100,
    profileImagePosition: migratedSettings.profileImagePosition || { x: 50, y: 50 },
    profileImageAlignment: migratedSettings.profileImageAlignment || 'center',
    profileImageBorder: migratedSettings.profileImageBorder || false,
    profileImageBorderColor: migratedSettings.profileImageBorderColor || '#333333',
    profileImageBorderThickness: migratedSettings.profileImageBorderThickness || 2,
    nameFontSize: migratedSettings.nameFontSize || 36,
    contactDisplayMode: migratedSettings.contactDisplayMode || 'text',
    topBoxColor: migratedSettings.topBoxColor || migratedSettings.leftColumnColor || '#f8f6f2', // Fallback auf leftColumnColor
    topBoxAlpha: migratedSettings.topBoxAlpha !== undefined ? migratedSettings.topBoxAlpha : 0, // Default: 100% transparent
    topBoxImage: migratedSettings.topBoxImage || '', // Kein Bild standardmäßig
    topBoxHeightMode: migratedSettings.topBoxHeightMode || 'nameBottom',
    leftColumnImage: migratedSettings.leftColumnImage || '', // Kein Bild standardmäßig
    titleFont: migratedSettings.titleFont || '"Times New Roman", Times, serif',
    bodyFont: migratedSettings.bodyFont || 'Arial, sans-serif',
    shortProfileTextLength: migratedSettings.shortProfileTextLength || 'S',
    workExperienceTextLength: migratedSettings.workExperienceTextLength || 'S',
    sortWorkExperienceByRelevance: migratedSettings.sortWorkExperienceByRelevance !== undefined ? migratedSettings.sortWorkExperienceByRelevance : false
  };

  return {
    template1: { ...baseSettings, templateName: 'Modern' },
    template2: { ...baseSettings, templateName: 'Professional' },
    template3: { ...baseSettings, templateName: 'Classic' },
    selectedTemplate: 'template1',
    lastUpdated: new Date().toISOString(),
    version: '2.0.0'
  };
};

// CV-Settings über Backend aktualisieren (lädt aktuelle Settings, merged Updates, speichert zurück)
export const updateCvSettings = async (cvId: string, updates: Partial<CvSettings>): Promise<void> => {
  try {
    const currentSettings = (await loadCvSettings(cvId)) || defaultCvSettings;
    const mergedSettings: CvSettings = {
      ...currentSettings,
      ...updates,
      lastUpdated: new Date().toISOString()
    };
    await saveCvSettings(cvId, mergedSettings);
  } catch (error) {
    console.error('Fehler beim Aktualisieren der CV-Settings:', error);
    throw error;
  }
};

// CV-Settings über Backend zurücksetzen
export const resetCvSettings = async (cvId: string): Promise<void> => {
  try {
    await saveCvSettings(cvId, defaultCvSettings);
  } catch (error) {
    console.error('Fehler beim Zurücksetzen der CV-Settings:', error);
    throw error;
  }
};

// Template-spezifische Settings zurücksetzen
export const resetTemplateSettings = async (cvId: string, templateId: 'template1' | 'template2' | 'template3'): Promise<void> => {
  try {
    const currentSettings = (await loadCvSettings(cvId)) || defaultCvSettings;
    const updatedSettings = {
      ...currentSettings,
      [templateId]: createDefaultDesignSettings(
        templateId === 'template1' ? 'Modern' : 
        templateId === 'template2' ? 'Professional' : 'Classic'
      )
    };
    await saveCvSettings(cvId, updatedSettings);
  } catch (error) {
    console.error('Fehler beim Zurücksetzen der Template-Settings:', error);
    throw error;
  }
};